import { useEffect } from 'react';
import {
  useDispatch,
  useLead,
  useLeadUserProperties,
  usePracticeProperties,
} from '@witmetrics/data-store';
import { isBlank } from '@witmetrics/utils/strings';
import { API } from '@/api';
import { showErrorToaster, showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useDate } from '@/hooks/useDate';
import { useString } from '@/hooks/useString';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import FormBlock from '@/components/FormBlock';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { classes, updatePatient } from './utils';

export type EditPatientDialogProps = {
  patientId: number;
  onSelect?: (patientId: number) => void;
  onClose: () => void;
};

export default function EditPatientDialog({
  patientId,
  onSelect,
  onClose,
}: EditPatientDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const practiceProperties = usePracticeProperties();
  const patient = useLead(patientId);
  const leadUserProperties = useLeadUserProperties(patientId);
  const [firstname, setFirstname] = useString();
  const [lastname, setLastname] = useString();
  const [externalRef, setExternalRef] = useString();
  const [email, setEmail] = useString();
  const [phone, setPhone] = useString();
  const [createdAt, setCreatedAt] = useDate(null, true);
  const { setInvalidFields, isValidField } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (patient) fillFields();
  }, [patient]);

  const fillFields = () => {
    if (!patient) return;
    setFirstname(patient.firstname);
    setLastname(patient.lastname);
    setExternalRef(patient.externalRef || '');
    setEmail(patient.email);
    setCreatedAt(patient.createdAt);
    setPhone((leadUserProperties.phone?.value as string) || '');
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      if (isBlank(firstname)) {
        toggleSaving(false);
        return setInvalidFields(['firstname']);
      }
      if (!isBlank(email)) {
        const emailData = await API.Users.validateEmail(email, patientId);
        if (!emailData.isValid) {
          showErrorToaster(emailData.message);
          toggleSaving(false);
          return setInvalidFields(['email']);
        }
      }
      dispatch.addLeads([
        await updatePatient(patientId, createdAt, {
          firstname,
          lastname,
          externalRef,
          email,
          properties: [
            { propertyId: practiceProperties.phone.id, value: phone },
          ],
        }),
      ]);
      showSuccessToaster('Patient updated');
      toggleSaving(false);
      onSelect?.(patientId);
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error updating patient', () => handleSave());
    }
  };

  const isSaveDisabled = () => {
    return isSaving || isBlank(firstname);
  };

  const renderTextInput = (
    name: string,
    label: string,
    value: string,
    onChange: (update: string) => void,
    autoFocus?: boolean
  ) => {
    return (
      <div className={classes.input}>
        <TextField
          autoFocus={autoFocus}
          error={!isValidField(name)}
          className={classes.textField}
          name={name}
          label={label}
          value={value}
          onChange={onChange}
        />
      </div>
    );
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="Edit Patient" onClose={onClose} />
      <DialogBody className={classes.body}>
        <FormBlock
          title="Patient Info"
          subtitle="Basic information about the patient">
          <div className={classes.section}>
            {renderTextInput(
              'firstname',
              'First Name',
              firstname,
              setFirstname,
              true
            )}
            {renderTextInput('lastname', 'Last Name', lastname, setLastname)}
            {renderTextInput(
              'externalRef',
              'External Ref.',
              externalRef,
              setExternalRef
            )}
            {renderTextInput('email', 'Email', email, setEmail)}
            {practiceProperties.phone
              ? renderTextInput('phone', 'Phone', phone, setPhone)
              : null}
            {patient && (
              <div className={classes.input}>
                <DateTimeInput
                  disablePast={false}
                  className={classes.selectDropdown}
                  label="Created At"
                  value={createdAt}
                  onChange={setCreatedAt}
                />
              </div>
            )}
          </div>
        </FormBlock>
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaveDisabled()}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
